import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  Stepper,
  Divider,
  LoadingOverlay,
  TextInput,
  Paper,
  Badge,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import {
  IconUpload,
  IconX,
  IconFile,
  IconDownload,
  IconCheck,
  IconSearch,
  IconCirclePlus
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { masterGroupService } from 'services/lia/masterGroupService';
import { useDebouncedValue } from '@mantine/hooks';
import * as XLSX from 'xlsx';
import QuestionPreviewTable from './QuestionPreviewTable';
import DataTables from 'components/DataTables';

/* eslint-disable */
interface ImportQuestionModalProps {
  opened: boolean;
  onClose: () => void;
  onImport: (groupId: string, questions: ImportQuestion[]) => Promise<void>;
}
/* eslint-enable */

export interface ImportQuestion {
  title: string;
  description?: string;
  type: 'Choose One' | 'Range' | 'Free-text';
  category?: string;
  skalaCategory?: string;
  rangeItems?: Array<{
    label: string;
    value: string;
    sequence: number;
  }>;
  selected: boolean;
  rowIndex: number;
}

const ImportQuestionModal: React.FC<ImportQuestionModalProps> = ({ opened, onClose, onImport }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedGroupName, setSelectedGroupName] = useState<string>('');
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupPage, setGroupPage] = useState(1);
  const [debouncedGroupSearch] = useDebouncedValue(groupSearchQuery, 300);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [parsedQuestions, setParsedQuestions] = useState<ImportQuestion[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [parseError, setParseError] = useState<string>('');

  // Fetch group questions with search and pagination
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['masterGroups', debouncedGroupSearch, groupPage],
    queryFn: () =>
      masterGroupService.getList({
        search: debouncedGroupSearch,
        page: groupPage,
        limit: 10
      }),
    enabled: opened,
    staleTime: 5 * 60 * 1000
  });

  // Group selection helper functions
  const groupColumns = [
    {
      heading: 'Group Name',
      props: 'title',
      sortable: true,
      active: true,
      disabled: false,
      id: 'title'
    },
    {
      heading: 'Description',
      props: 'desc',
      sortable: false,
      active: true,
      disabled: false,
      id: 'desc'
    },
    {
      heading: 'Actions',
      props: 'actions',
      sortable: false,
      active: true,
      disabled: false,
      id: 'actions'
    }
  ];

  const handleGroupSelect = (group: any) => {
    setSelectedGroupId(group.msGroupQuestionID);
    setSelectedGroupName(group.title);
  };

  const handleGroupPageChange = (page: number) => {
    setGroupPage(page);
  };

  const prepareGroupTableData = (groups: any[]) => {
    return groups.map((group) => ({
      ...group,
      actions: (
        <Tooltip
          label={selectedGroupId === group.msGroupQuestionID ? 'Selected' : 'Select this group'}
          position='top'
          withArrow
        >
          <ActionIcon
            size='lg'
            variant={selectedGroupId === group.msGroupQuestionID ? 'filled' : 'outline'}
            color={selectedGroupId === group.msGroupQuestionID ? 'green' : '#7B2183'}
            onClick={() => handleGroupSelect(group)}
            disabled={selectedGroupId === group.msGroupQuestionID}
          >
            {selectedGroupId === group.msGroupQuestionID ? (
              <IconCheck size={16} />
            ) : (
              <IconCirclePlus size={16} />
            )}
          </ActionIcon>
        </Tooltip>
      )
    }));
  };

  const handleFileUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setUploadedFile(file);
    setParseError('');

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      const questions: ImportQuestion[] = [];
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i] as any[];
        if (!row[0]) continue;

        const question: ImportQuestion = {
          title: row[0] || '',
          description: row[1] || '',
          type: (row[2] as 'Choose One' | 'Range' | 'Free-text') || 'Free-text',
          category: row[3] || '',
          skalaCategory: row[4] || '',
          selected: true,
          rowIndex: i + 1
        };

        if (question.type === 'Range' && row[5]) {
          try {
            const rangeItemsText = row[5];
            const items = rangeItemsText.split(',').map((item: string, index: number) => {
              const [label, value] = item.split(':');
              return {
                label: label?.trim() || '',
                value: value?.trim() || (index + 1).toString(),
                sequence: index + 1
              };
            });
            question.rangeItems = items;
          } catch {
            setParseError(
              'Invalid range items format. Please use "label:value" format for each item.'
            );
          }
        }

        questions.push(question);
      }

      setParsedQuestions(questions);
      setCurrentStep(2);
    } catch {
      setParseError('Failed to parse Excel file. Please check the file format.');
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      [
        'Question Title*',
        'Description',
        'Type*',
        'Categories (comma-separated)',
        'Skala Category',
        'Range Items (for Range type only)'
      ],
      [
        'How would you rate your communication skills?',
        'Assess your ability to communicate effectively with colleagues and stakeholders',
        'Range',
        'Communication, Soft Skills, Performance',
        'Performance Rating Scale',
        'Poor:1,Fair:2,Good:3,Excellent:4,Outstanding:5'
      ],
      [
        'What are your key strengths in project management?',
        'List your main professional strengths and competencies',
        'Free-text',
        'Self Assessment, Project Management, Leadership',
        'Open Response Category',
        ''
      ],
      [
        'Which department best describes your current role?',
        'Select your primary working department or division',
        'Free-text',
        'Department, Organization, Classification',
        'Department Selection',
        ''
      ],
      [
        'Rate your technical proficiency in your field',
        'Evaluate your technical skills and expertise level',
        'Range',
        'Technical Skills, Expertise, A1, A2',
        'Proficiency Scale',
        'Beginner:1,Intermediate:2,Advanced:3,Expert:4'
      ]
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Questions Template');
    XLSX.writeFile(wb, 'questions_import_template.xlsx');
  };

  const handleImport = async () => {
    const selectedQuestions = parsedQuestions.filter((q) => q.selected);
    if (selectedQuestions.length === 0) {
      setParseError('Please select at least one question to import.');
      return;
    }

    if (!selectedGroupId) {
      setParseError('Please select a question group.');
      return;
    }

    setIsProcessing(true);
    try {
      await onImport(selectedGroupId, selectedQuestions);
      handleClose();
    } catch {
      setParseError('Failed to import questions. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setSelectedGroupId('');
    setSelectedGroupName('');
    setGroupSearchQuery('');
    setGroupPage(1);
    setUploadedFile(null);
    setParsedQuestions([]);
    setParseError('');
    setIsProcessing(false);
    onClose();
  };

  const canProceedToStep2 = selectedGroupId !== '';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap='md' align='center'>
          <div
            style={{
              padding: '10px',
              background: 'linear-gradient(135deg, #7B2183 0%, #9C3493 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 8px rgba(123, 33, 131, 0.3)'
            }}
          >
            <IconUpload size={20} color='white' />
          </div>
          <Text fw={700} size='xl' c='#2C2E33'>
            Import Questions
          </Text>
        </Group>
      }
      size='xl'
      centered
      styles={{
        header: {
          background: 'linear-gradient(135deg, #FAFBFC 0%, #F8F9FA 100%)',
          borderBottom: '1px solid #E9ECEF',
          padding: '24px 28px',
          marginBottom: 0,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px'
        },
        body: {
          padding: '0px',
          minHeight: '70vh',
          backgroundColor: '#FFFFFF'
        },
        content: {
          borderRadius: '16px',
          border: '1px solid #E9ECEF',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <LoadingOverlay visible={isLoadingGroups || isProcessing} />

      <div style={{ padding: '28px' }}>
        <Stepper
          active={currentStep}
          onStepClick={setCurrentStep}
          allowNextStepsSelect={false}
          size='lg'
          color='#7B2183'
          completedIcon={<IconCheck size={16} />}
          styles={{
            root: {
              marginBottom: '32px'
            },
            step: {
              padding: '8px'
            },
            stepLabel: {
              fontSize: '14px',
              fontWeight: 500,
              color: '#495057'
            },
            stepDescription: {
              fontSize: '12px',
              color: '#868E96',
              marginTop: '4px'
            },
            stepIcon: {
              borderWidth: '1px',
              fontSize: '12px',
              fontWeight: 600,
              width: '32px',
              height: '32px'
            },
            separator: {
              height: '1px',
              backgroundColor: '#DEE2E6'
            }
          }}
        >
          <Stepper.Step label='Group' description='Select group'>
            <div
              style={{
                padding: '24px',
                backgroundColor: '#F8F9FA',
                borderRadius: '12px',
                border: '1px solid #E9ECEF',
                marginTop: '24px'
              }}
            >
              <Stack gap='lg'>
                <Group gap='md' align='center' mb='lg'>
                  <div
                    style={{
                      padding: '8px',
                      backgroundColor: '#7B2183',
                      borderRadius: '8px',
                      display: 'flex'
                    }}
                  >
                    <IconFile size={16} color='white' />
                  </div>
                  <Text fw={600} size='md' c='#2C2E33'>
                    Choose Question Group
                  </Text>
                </Group>

                <Stack gap='md'>
                  <div>
                    <Text fw={600} size='sm' c='#2C2E33' mb={8}>
                      Question Group <span style={{ color: '#FA5252' }}>*</span>
                    </Text>
                    <Text size='xs' c='#868E96' mb={16}>
                      All imported questions will be added to this group
                    </Text>

                    {selectedGroupName && (
                      <Paper
                        withBorder
                        p='md'
                        mb='md'
                        bg='linear-gradient(135deg, #E8F5E8 0%, #F0F9F0 100%)'
                        style={{ border: '1px solid #51CF66' }}
                      >
                        <Group>
                          <Badge
                            color='green'
                            variant='filled'
                            size='lg'
                            style={{ borderRadius: '8px' }}
                          >
                            <Group gap={4}>
                              <IconCheck size={14} />
                              Selected Group
                            </Group>
                          </Badge>
                          <Text size='sm' fw={600} c='#2F9E41'>
                            {selectedGroupName}
                          </Text>
                        </Group>
                      </Paper>
                    )}

                    <TextInput
                      placeholder='Search groups by name or description...'
                      value={groupSearchQuery}
                      onChange={(event) => {
                        setGroupSearchQuery(event.currentTarget.value);
                        setGroupPage(1);
                      }}
                      leftSection={<IconSearch size={16} color='#868E96' />}
                      mb='md'
                      size='md'
                      styles={{
                        input: {
                          borderRadius: '10px',
                          border: '1px solid #E9ECEF',
                          fontSize: '14px',
                          backgroundColor: '#FAFBFC',
                          '&:focus': {
                            borderColor: '#7B2183',
                            backgroundColor: '#FFFFFF'
                          }
                        }
                      }}
                    />

                    <Paper
                      withBorder
                      style={{
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid #E9ECEF'
                      }}
                    >
                      <DataTables
                        data={prepareGroupTableData(groupsData?.data || [])}
                        columns={groupColumns}
                        totalData={groupsData?.totalPages ? groupsData.totalPages * 10 : 0}
                        currentPage={groupPage}
                        handleChangePage={handleGroupPageChange}
                        loading={isLoadingGroups}
                        emptyMessageTitle='No Groups Found'
                        emptyMessageDescription={
                          groupSearchQuery
                            ? `No groups found matching "${groupSearchQuery}"`
                            : 'No groups available for import'
                        }
                        withPagination
                        currentSize={10}
                      />
                    </Paper>
                  </div>
                </Stack>

                <Group justify='flex-end' mt='xl'>
                  <Button
                    onClick={() => setCurrentStep(1)}
                    disabled={!canProceedToStep2}
                    size='lg'
                    radius='lg'
                    styles={{
                      root: {
                        background: !canProceedToStep2
                          ? '#CED4DA'
                          : 'linear-gradient(135deg, #7B2183 0%, #9C3493 100%)',
                        border: 'none',
                        fontSize: '15px',
                        fontWeight: 600,
                        height: '48px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        boxShadow: !canProceedToStep2
                          ? 'none'
                          : '0 4px 12px rgba(123, 33, 131, 0.3)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: !canProceedToStep2 ? 'none' : 'translateY(-1px)',
                          boxShadow: !canProceedToStep2
                            ? 'none'
                            : '0 6px 16px rgba(123, 33, 131, 0.4)'
                        }
                      }
                    }}
                  >
                    Continue
                  </Button>
                </Group>
              </Stack>
            </div>
          </Stepper.Step>

          <Stepper.Step label='Upload' description='Add file'>
            <Stack gap='lg' mt='xl'>
              <Group gap='md' align='center'>
                <div
                  style={{
                    padding: '8px',
                    backgroundColor: '#7B2183',
                    borderRadius: '8px',
                    display: 'flex'
                  }}
                >
                  <IconDownload size={16} color='white' />
                </div>
                <Text fw={600} size='md' c='#2C2E33'>
                  Upload Your File
                </Text>
              </Group>

              <div
                style={{
                  padding: '20px',
                  backgroundColor: '#F8F9FA',
                  borderRadius: '12px',
                  border: '1px solid #E9ECEF',
                  textAlign: 'center'
                }}
              >
                <Button
                  leftSection={<IconDownload size={16} />}
                  variant='outline'
                  onClick={downloadTemplate}
                  size='md'
                  styles={{
                    root: {
                      borderRadius: '8px',
                      borderColor: '#7B2183',
                      color: '#7B2183',
                      fontSize: '14px',
                      fontWeight: 600,
                      height: '42px',
                      '&:hover': {
                        backgroundColor: '#F3F0FF'
                      }
                    }
                  }}
                >
                  Download Excel Template
                </Button>

                <Text size='sm' c='#868E96' mt='md'>
                  Use the template to ensure proper formatting
                </Text>
              </div>

              <Divider
                label={
                  <Text size='sm' fw={600} c='#868E96'>
                    OR UPLOAD YOUR FILE
                  </Text>
                }
                labelPosition='center'
                styles={{
                  label: {
                    fontSize: '12px',
                    letterSpacing: '0.5px'
                  }
                }}
              />

              <Dropzone
                onDrop={handleFileUpload}
                accept={[
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  'application/vnd.ms-excel'
                ]}
                multiple={false}
                styles={{
                  root: {
                    borderRadius: '12px',
                    borderStyle: 'dashed',
                    borderWidth: '2px',
                    borderColor: '#DDD',
                    backgroundColor: '#FAFAFA',
                    transition: 'all 0.2s ease',
                    '&[data-accept]': {
                      borderColor: '#51cf66',
                      backgroundColor: '#F3F9F3'
                    },
                    '&[data-reject]': {
                      borderColor: '#ff6b6b',
                      backgroundColor: '#FFF5F5'
                    },
                    '&:hover': {
                      borderColor: '#7B2183',
                      backgroundColor: '#F8F9FA'
                    }
                  }
                }}
              >
                <Group justify='center' gap='lg' mih={160} style={{ pointerEvents: 'none' }}>
                  <Dropzone.Accept>
                    <IconUpload size={40} stroke={1.5} color='#51cf66' />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconX size={40} stroke={1.5} color='#ff6b6b' />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <IconFile size={40} stroke={1.5} color='#868E96' />
                  </Dropzone.Idle>

                  <div style={{ textAlign: 'center' }}>
                    <Text size='md' fw={600} mb='xs' c='#2C2E33'>
                      Drop your Excel file here
                    </Text>
                    <Text size='sm' c='#868E96' mb='xs'>
                      or click to browse files
                    </Text>
                    <Text size='xs' c='#ADB5BD'>
                      Supported: .xlsx, .xls (Max 10MB)
                    </Text>
                  </div>
                </Group>
              </Dropzone>

              {uploadedFile && (
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: '#F3F9F3',
                    borderRadius: '8px',
                    border: '1px solid #51cf66'
                  }}
                >
                  <Group gap='sm' align='center'>
                    <div
                      style={{
                        padding: '4px',
                        backgroundColor: '#51cf66',
                        borderRadius: '4px',
                        display: 'flex'
                      }}
                    >
                      <IconCheck size={14} color='white' />
                    </div>
                    <div>
                      <Text size='sm' fw={600} c='#2B8A3E'>
                        File uploaded successfully!
                      </Text>
                      <Text size='xs' c='#51cf66'>
                        {uploadedFile.name}
                      </Text>
                    </div>
                  </Group>
                </div>
              )}

              <Group justify='space-between' mt='xl'>
                <Button
                  variant='outline'
                  onClick={() => setCurrentStep(0)}
                  size='md'
                  styles={{
                    root: {
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      height: '42px',
                      borderColor: '#DDD',
                      color: '#495057'
                    }
                  }}
                >
                  Back
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label='Review' description='Confirm import'>
            <Stack gap='lg' mt='xl'>
              <Group gap='md' align='center'>
                <div
                  style={{
                    padding: '8px',
                    backgroundColor: '#7B2183',
                    borderRadius: '8px',
                    display: 'flex'
                  }}
                >
                  <IconFile size={16} color='white' />
                </div>
                <Text fw={600} size='md' c='#2C2E33'>
                  Review Questions
                </Text>
              </Group>

              <QuestionPreviewTable
                questions={parsedQuestions}
                onQuestionsChange={setParsedQuestions}
              />

              <div
                style={{
                  padding: '20px',
                  backgroundColor: '#F8F9FA',
                  borderRadius: '12px',
                  border: '1px solid #E9ECEF'
                }}
              >
                <Group justify='space-between' align='center'>
                  <Button
                    variant='outline'
                    onClick={() => setCurrentStep(1)}
                    size='md'
                    styles={{
                      root: {
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 600,
                        height: '42px',
                        borderColor: '#DDD',
                        color: '#495057'
                      }
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={parsedQuestions.filter((q) => q.selected).length === 0}
                    size='md'
                    loading={isProcessing}
                    styles={{
                      root: {
                        borderRadius: '8px',
                        backgroundColor: '#7B2183',
                        fontSize: '14px',
                        fontWeight: 600,
                        height: '42px',
                        paddingLeft: '24px',
                        paddingRight: '24px'
                      }
                    }}
                  >
                    Import {parsedQuestions.filter((q) => q.selected).length} Question
                    {parsedQuestions.filter((q) => q.selected).length !== 1 ? 's' : ''}
                  </Button>
                </Group>
              </div>
            </Stack>
          </Stepper.Step>
        </Stepper>

        {parseError && (
          <div
            style={{
              padding: '16px',
              backgroundColor: '#FFF5F5',
              borderRadius: '8px',
              border: '1px solid #ff6b6b',
              marginTop: '20px'
            }}
          >
            <Group gap='sm' align='flex-start'>
              <div
                style={{
                  padding: '4px',
                  backgroundColor: '#ff6b6b',
                  borderRadius: '4px',
                  display: 'flex',
                  marginTop: '2px'
                }}
              >
                <IconX size={14} color='white' />
              </div>
              <div>
                <Text size='sm' fw={600} c='#C92A2A' mb='xs'>
                  Import Error
                </Text>
                <Text size='sm' c='#E03131'>
                  {parseError}
                </Text>
              </div>
            </Group>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImportQuestionModal;
